import { DELAY_PANEL, DELAY_STAT_STEP } from '@savvy-kit/foundations/motion/tokens';

import type { ChartConfig } from '@savvy-kit/patterns/chart';
import type { PortfolioItemGroupName } from '@savvy/constants/generated/gql-enums';
import {
  PERFORMANCE_TIME_PERIOD_ALL_TIME,
  PERFORMANCE_TIME_PERIOD_PAST_MONTH,
  PERFORMANCE_TIME_PERIOD_PAST_THREE_MONTHS,
  PERFORMANCE_TIME_PERIOD_PAST_YEAR,
  PERFORMANCE_TIME_PERIOD_YEAR_TO_DATE,
} from '@savvy/constants/performance-time-periods';
import {
  PORTFOLIO_ITEM_GROUP_OTHER_INVESTMENTS,
  PORTFOLIO_ITEM_GROUP_SAVVY_ACCOUNTS,
} from '@savvy/constants/portfolio-item-groups';

import type { ClientPortalTimeRange } from '../common/constants';
import type { ClientPortalElementName } from '../shell/types';
import type { HomeDayChangeReason } from './types';

export const HOME_MANAGED_GROUP: PortfolioItemGroupName = PORTFOLIO_ITEM_GROUP_SAVVY_ACCOUNTS;
export const HOME_HELD_AWAY_GROUP: PortfolioItemGroupName = PORTFOLIO_ITEM_GROUP_OTHER_INVESTMENTS;

export const HOME_INVESTMENT_GROUPS: readonly PortfolioItemGroupName[] = [HOME_MANAGED_GROUP, HOME_HELD_AWAY_GROUP];

export const HOME_CHART_CONFIG = {
  total: { label: 'Total', color: 'var(--graph-line-accent)' },
  managed: { label: 'Advisor-managed', color: 'var(--graph-line-accent)' },
  heldAway: { label: 'Held away', color: 'var(--secondary)' },
} satisfies ChartConfig;

export const HOME_CHART_GRADIENT_ID = 'clientPortalHomeValueGradient';

export const HOME_CHART_X_TICK_MIN_GAP = 32;

export const HOME_CHART_Y_AXIS_WIDTH = 60;

export const HOME_CHART_Y_TICK_COUNT = 5;

export const HOME_PERIOD_CAPTIONS: Readonly<Record<ClientPortalTimeRange, string>> = {
  past_month: 'past month',
  past_three_months: 'past 3 months',
  year_to_date: 'year to date',
  past_year: 'past year',
  all_time: 'since inception',
};

export const DAY_CHANGE_MAX_AGE_DAYS = 3;

export const DAY_CHANGE_MAX_PERCENT = 5;

export const HOME_DAY_CHANGE_LABEL = 'Today';

export function homeDayChangeUpdatedLabel(dateLabel: string) {
  return `Last updated: ${dateLabel}`;
}

export const HOME_DAY_CHANGE_REASON_MESSAGES: Readonly<Record<HomeDayChangeReason, string>> = {
  no_history: 'Not enough history yet',
  stale_balances: 'Waiting on current balances',
  basis_change: 'Not available today',
};

export const HOME_DAY_CHANGE_TOOLTIP_LABEL = 'How this change is measured';

export const HOME_DAY_CHANGE_NO_READING_NOTE = 'We need two balance readings before we can show a change.';

export const HOME_DAY_CHANGE_REFRESH_NOTE =
  'Balances refresh after each market close, so the latest day appears the next business day.';

export function homeDayChangeWindowNote(fromLabel: string, toLabel: string) {
  return `Measured from your ${fromLabel} balance to your ${toLabel} balance.`;
}

export function homeManagedSubtitle(brandName: string): string {
  return `${brandName}-managed`;
}

export const HOME_HELD_AWAY_MANAGEMENT = 'Managed by household';

export const HOME_VALUE_ERROR_TITLE = 'We couldn’t load your balance history';

export const HOME_VALUE_ERROR_DESCRIPTION =
  'Your accounts and balances are safe — this is just the chart. Try again in a moment.';

export const HOME_INVESTMENTS_TITLE = 'Your investments';

export const HOME_INVESTMENTS_VIEW_ACCOUNTS_LABEL = 'View accounts';

export const HOME_INVESTMENTS_EMPTY_MESSAGE = 'No investment accounts are connected yet.';

export const HOME_INVESTMENTS_ERROR_TITLE = 'We couldn’t load your accounts';

export const HOME_INVESTMENT_ROW_ELEMENT_NAME: ClientPortalElementName = 'client-portal-home-investment-row-link';

export const HOME_TASK_ROW_ELEMENT_NAME: ClientPortalElementName = 'client-portal-home-task-row';

export const HOME_RANGE_ELEMENT_NAMES: Readonly<Record<ClientPortalTimeRange, ClientPortalElementName>> = {
  [PERFORMANCE_TIME_PERIOD_PAST_MONTH]: 'client-portal-home-range-1m',
  [PERFORMANCE_TIME_PERIOD_PAST_THREE_MONTHS]: 'client-portal-home-range-3m',
  [PERFORMANCE_TIME_PERIOD_YEAR_TO_DATE]: 'client-portal-home-range-ytd',
  [PERFORMANCE_TIME_PERIOD_PAST_YEAR]: 'client-portal-home-range-1y',
  [PERFORMANCE_TIME_PERIOD_ALL_TIME]: 'client-portal-home-range-all',
};

export const HOME_CHART_SINGLE_READING_MESSAGE = 'There isn’t enough history to chart this range yet.';

export const HOME_CHART_NO_HISTORY_TITLE = 'No balance history yet';

export const HOME_CHART_NO_HISTORY_MESSAGE = 'Your chart will appear here once we’ve tracked a few days of balances.';

export function homeStaleBalancesPrefix(asOfLabel: string) {
  return `These balances are from ${asOfLabel}.`;
}

export const HOME_STALE_BALANCES_SUFFIX =
  'We’re working to refresh them — nothing has changed with your money in the meantime.';

export const HOME_ALLOCATION_TITLE = "How you're invested";

export const HOME_ALLOCATION_VIEW_DETAILS_LABEL = 'View details';

export const HOME_ALLOCATION_EMPTY_MESSAGE = 'We don’t have an allocation breakdown for your accounts yet.';

export const HOME_ALLOCATION_ERROR_TITLE = 'We couldn’t load your allocation';

export const HOME_ALLOCATION_LEGEND_CAP = 6;

export const HOME_ALLOCATION_SEE_ALL_LABEL = 'See all';

export const HOME_ALLOCATION_SHOW_LESS_LABEL = 'Show less';

export const HOME_TASKS_PANEL_CAP = 5;

export const HOME_TASKS_PANEL_TITLE = 'Waiting on you';

export const HOME_TASKS_EMPTY_MESSAGE = 'Nothing needed from you right now';

export const HOME_TASKS_ERROR_TITLE = 'We couldn’t load your tasks';

export const HOME_TASKS_VIEW_ALL_LABEL = 'View all tasks';

export const HOME_TASK_OVERDUE_LABEL = 'Overdue';

export const HOME_TASK_NO_DUE_DATE_LABEL = 'No due date';

export const HOME_TASKS_PANEL_SHEEN_CLASS = 'bg-linear-to-bl from-primary/5 via-transparent to-transparent';

// The order the page resolves in, after the value card's own beats: the supporting tiles sweep, then the
// bottom row (investments, allocation) follows them, and the tasks panel is last of all, arriving as the
// bottom row finishes so the reader is never pulled sideways mid-sweep.
export const HOME_BOTTOM_ROW_DELAY = DELAY_PANEL;
export const HOME_TASKS_DELAY = DELAY_PANEL + 3 * DELAY_STAT_STEP;
